import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AlertCircle, Loader2, Package, Search } from 'lucide-react';
import axiosInstance from '../../../../redux/api';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../ui/dialog';
import { Button } from './primitives';
import { CURRENCY, formatAmount } from '../shared/orderFormat';
import { ITEM_QTY_MAX, ITEM_QTY_MIN } from '../shared/orderConstants';

/*
 * Adds a live product to an existing order.
 *
 * The backend prices the line itself, at the product's current selling price —
 * so the price shown here is a preview of what will be charged, not something
 * the operator sets. It refuses products that aren't live, that are already an
 * active line on this order, or that don't have the stock; those refusals are
 * shown verbatim because they say what to do instead.
 *
 * Search is debounced and request-id guarded: type quickly and only the last
 * response is allowed to render, so results can't arrive out of order.
 */

const SEARCH_DEBOUNCE_MS = 350;
const RESULT_LIMIT = 20;

const AddItemDialog = ({ open, onOpenChange, existingProductIds, onAdd }) => {
  const [term, setTerm] = useState('');
  const [results, setResults] = useState([]);
  const [isSearching, setSearching] = useState(false);
  const [selected, setSelected] = useState(null);
  const [quantity, setQuantity] = useState('1');
  const [error, setError] = useState('');
  const [isSubmitting, setSubmitting] = useState(false);

  const requestRef = useRef(0);

  useEffect(() => {
    if (!open) return;
    setTerm('');
    setResults([]);
    setSelected(null);
    setQuantity('1');
    setError('');
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const query = term.trim();
    if (query.length < 2) {
      setResults([]);
      setSearching(false);
      return undefined;
    }

    setSearching(true);
    const requestId = ++requestRef.current;
    const timer = setTimeout(async () => {
      try {
        const response = await axiosInstance.get('admin/products', {
          params: {
            page: 1,
            per_page: RESULT_LIMIT,
            search_key: query,
            status: 1,
          },
        });
        if (requestId !== requestRef.current) return;
        const payload = response?.data?.response;
        setResults(Array.isArray(payload?.products) ? payload.products : []);
      } catch {
        if (requestId !== requestRef.current) return;
        setResults([]);
      } finally {
        if (requestId === requestRef.current) setSearching(false);
      }
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [term, open]);

  const submit = useCallback(async () => {
    if (!selected) return;
    const qty = Number(quantity);
    if (!Number.isFinite(qty) || qty < ITEM_QTY_MIN || qty > ITEM_QTY_MAX) {
      setError(`Quantity must be between ${ITEM_QTY_MIN} and ${ITEM_QTY_MAX}.`);
      return;
    }
    setSubmitting(true);
    setError('');
    const result = await onAdd({ productId: selected.id, quantity: qty });
    setSubmitting(false);
    if (result?.ok) onOpenChange(false);
    else setError(result?.message || 'That item could not be added.');
  }, [selected, quantity, onAdd, onOpenChange]);

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => !isSubmitting && onOpenChange(next)}
    >
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-base font-semibold text-nysmah-dark">
            Add an item
          </DialogTitle>
          <DialogDescription className="text-[12.5px] text-nysmah-muted">
            The product is added at its current selling price. Stock is reserved
            straight away.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-3">
          <div className="relative">
            <Search
              className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-nysmah-muted pointer-events-none"
              aria-hidden="true"
            />
            <input
              type="search"
              value={term}
              autoFocus
              onChange={(event) => setTerm(event.target.value)}
              placeholder="Search by name or barcode…"
              aria-label="Search products"
              className="w-full h-9 pl-9 pr-3 rounded-lg border border-[#D6DFE2] bg-white text-[13px] text-nysmah-dark placeholder:text-nysmah-muted focus:outline-none focus:border-nysmah-accent focus:ring-1 focus:ring-nysmah-accent"
            />
          </div>

          <div className="max-h-64 overflow-y-auto rounded-lg border border-[#E1E7EA]">
            {term.trim().length < 2 ? (
              <p className="m-0 px-3 py-6 text-center text-[12.5px] text-nysmah-muted">
                Type at least two characters to search.
              </p>
            ) : isSearching ? (
              <p className="m-0 px-3 py-6 flex items-center justify-center gap-2 text-[12.5px] text-nysmah-muted">
                <Loader2 className="w-3.5 h-3.5 animate-spin" aria-hidden="true" />
                Searching…
              </p>
            ) : results.length === 0 ? (
              <p className="m-0 px-3 py-6 text-center text-[12.5px] text-nysmah-muted">
                No live products match that.
              </p>
            ) : (
              <ul className="m-0 p-0 list-none">
                {results.map((product) => {
                  const alreadyOn = existingProductIds.has(Number(product.id));
                  const isSelected = selected?.id === product.id;
                  return (
                    <li key={product.id}>
                      <button
                        type="button"
                        disabled={alreadyOn}
                        onClick={() => setSelected(product)}
                        className={`w-full flex items-center gap-2.5 px-3 py-2 text-left border-0 border-b border-[#F0F4F5] cursor-pointer transition-colors disabled:cursor-not-allowed ${
                          isSelected
                            ? 'bg-[#EFF4F6]'
                            : 'bg-white hover:bg-[#F7FAFA] disabled:bg-white'
                        }`}
                      >
                        <span className="w-8 h-8 rounded-md border border-[#E1E7EA] bg-[#F5F8F8] overflow-hidden shrink-0 flex items-center justify-center">
                          {product.image ? (
                            <img
                              src={product.image}
                              alt=""
                              className="w-full h-full object-cover"
                              loading="lazy"
                            />
                          ) : (
                            <Package
                              className="w-3.5 h-3.5 text-[#C4CFD5]"
                              aria-hidden="true"
                            />
                          )}
                        </span>
                        <span className="min-w-0 flex-1">
                          <span
                            className={`block text-[12.5px] font-semibold truncate ${
                              alreadyOn ? 'text-nysmah-muted' : 'text-nysmah-dark'
                            }`}
                          >
                            {product.name}
                          </span>
                          <span className="block text-[11px] text-nysmah-muted tabular-nums truncate">
                            {alreadyOn
                              ? 'Already on this order — change its quantity instead'
                              : product.barcode || '—'}
                          </span>
                        </span>
                        <span className="text-[12.5px] font-bold text-nysmah-dark tabular-nums shrink-0">
                          {formatAmount(product.sale_price ?? product.price)}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          {selected && (
            <div className="flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg bg-[#F5F8F8] border border-[#E1E7EA]">
              <span className="min-w-0">
                <span className="block text-[10.5px] font-bold uppercase tracking-[0.08em] text-[#6B7C86]">
                  Adding
                </span>
                <span className="block text-[13px] font-semibold text-nysmah-dark truncate">
                  {selected.name}
                </span>
              </span>
              <label className="flex items-center gap-2 shrink-0">
                <span className="text-[12px] font-medium text-[#6B7C86]">Qty</span>
                <input
                  type="text"
                  inputMode="numeric"
                  value={quantity}
                  onChange={(event) =>
                    setQuantity(event.target.value.replace(/[^\d]/g, ''))
                  }
                  className="w-14 h-8 text-center rounded-md border border-[#D6DFE2] bg-white text-[13px] font-semibold text-nysmah-dark tabular-nums focus:outline-none focus:border-nysmah-accent focus:ring-1 focus:ring-nysmah-accent"
                />
              </label>
            </div>
          )}

          {error && (
            <div
              role="alert"
              className="flex items-start gap-2 px-3 py-2 rounded-lg border border-[#EFC4C4] bg-[#FCF2F2] text-[12.5px] text-[#C2453D]"
            >
              <AlertCircle
                className="w-3.5 h-3.5 shrink-0 mt-0.5"
                aria-hidden="true"
              />
              <span>{error}</span>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={submit}
            disabled={!selected || isSubmitting}
          >
            {isSubmitting ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" aria-hidden="true" />
            ) : null}
            {isSubmitting
              ? 'Adding…'
              : selected
                ? `Add for ${CURRENCY} ${formatAmount(
                    (Number(selected.sale_price ?? selected.price) || 0) *
                      (Number(quantity) || 0),
                  )}`
                : 'Add item'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AddItemDialog;
